import React from 'react';
import { motion } from 'framer-motion';
import { Plus, Play, Sparkles, X } from 'lucide-react';
import { ARCHETYPES } from './archetypeData';
import sonicCues from '../Premium/sonicCues';

const StateLibrary = ({ savedStates = [], onCreateNew, onPlayState, onClose }) => {
  return (
    <div
      className="fixed inset-0 flex flex-col z-50"
      style={{
        background:
          "radial-gradient(ellipse at 50% 0%, #18213A 0%, #0a1224 50%, #03060A 100%)",
      }}
      data-testid="state-library"
    >
      {/* Close chip */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={() => {
          sonicCues.tap();
          onClose();
        }}
        className="rec-chip absolute top-6 right-6 z-10"
        data-testid="close-reality-btn"
        style={{ padding: "0.5rem 0.7rem" }}
        aria-label="Close"
      >
        <X size={14} />
      </motion.button>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-6 py-10 pb-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12 mt-6"
        >
          <p
            className="uppercase tracking-[0.32em] mb-3"
            style={{
              color: "#D7BB7A",
              fontFamily: '"Manrope", sans-serif',
              fontSize: "0.58rem",
            }}
          >
            Reality States · Library
          </p>
          <h1
            className="text-3xl font-light mb-3 italic"
            style={{
              color: "#F3EDDD",
              fontFamily: '"Cormorant Garamond", Georgia, serif',
              letterSpacing: "0.04em",
            }}
          >
            Step into reality
          </h1>
          <p
            className="italic"
            style={{
              color: "rgba(243, 237, 221, 0.55)",
              fontFamily: '"Cormorant Garamond", serif',
              fontSize: "0.95rem",
            }}
          >
            Tune into the version of you that already exists.
          </p>
        </motion.div>

        {/* Saved States */}
        {savedStates.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-10 max-w-xl mx-auto"
          >
            <p
              className="uppercase mb-4"
              style={{
                color: "#D7BB7A",
                fontFamily: '"Manrope", sans-serif',
                fontSize: "0.58rem",
                letterSpacing: "0.28em",
              }}
            >
              Your Realities
            </p>
            <div className="space-y-3">
              {savedStates.map((state, index) => {
                const archetype = ARCHETYPES[state.archetypeId];
                return (
                  <motion.button
                    key={state.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.08 * index, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -2 }}
                    onClick={() => {
                      sonicCues.threshold();
                      onPlayState(state);
                    }}
                    className="rec-surface w-full p-4 flex items-center gap-4 group transition-all duration-500"
                    style={{
                      borderColor: archetype?.color
                        ? `${archetype.color}55`
                        : "rgba(215, 187, 122, 0.16)",
                    }}
                    data-testid={`saved-state-${state.id}`}
                  >
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl transition-transform group-hover:scale-110"
                      style={{
                        backgroundColor: archetype?.color
                          ? `${archetype.color}1f`
                          : "rgba(215, 187, 122, 0.12)",
                        border: archetype?.color
                          ? `1px solid ${archetype.color}44`
                          : "1px solid rgba(215, 187, 122, 0.2)",
                      }}
                    >
                      {archetype?.emoji || '✦'}
                    </div>
                    <div className="flex-1 text-left">
                      <p
                        className="italic mb-0.5"
                        style={{
                          color: "#F3EDDD",
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: "1.15rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {state.name}
                      </p>
                      <p
                        className="italic opacity-70"
                        style={{
                          color: "rgba(243, 237, 221, 0.55)",
                          fontFamily: '"Cormorant Garamond", serif',
                          fontSize: "0.88rem",
                        }}
                      >
                        {archetype?.name || 'Custom'}
                      </p>
                    </div>
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                      style={{
                        backgroundColor: "rgba(215, 187, 122, 0.14)",
                        border: "1px solid rgba(215, 187, 122, 0.32)",
                      }}
                    >
                      <Play size={14} style={{ color: "#D7BB7A", marginLeft: 1 }} />
                    </div>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Subtle decorative element */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="flex justify-center mt-8 mb-4"
        >
          <Sparkles size={14} style={{ color: "rgba(215, 187, 122, 0.4)" }} />
        </motion.div>
      </div>

      {/* Fixed Create New Reality CTA — gilded chip */}
      <div
        className="flex-shrink-0 p-6 pb-28 flex justify-center"
        style={{
          background:
            "linear-gradient(to top, #03060A 0%, rgba(3, 6, 10, 0.85) 60%, transparent 100%)",
        }}
      >
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          whileHover={{ y: -2 }}
          onClick={() => {
            sonicCues.threshold();
            onCreateNew();
          }}
          className="rec-chip"
          data-active="true"
          data-testid="create-new-reality-btn"
          style={{ padding: "0.85rem 1.7rem", fontSize: "0.66rem" }}
        >
          <Plus size={14} />
          Create New Reality
        </motion.button>
      </div>
    </div>
  );
};

export default StateLibrary;
